/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { createContext, useContext, useState } from 'react';
import {
  bhairavaTheme,
  rudraTheme,
  neelkanthTheme,
  mahakalaTheme,
  gangadharTheme,
  natarajaTheme,
  agniTheme
} from './themes';

// The app-wide default. Exported so the theme switcher can label it rather
// than hardcoding a name that would drift the next time this changes.
export const DEFAULT_THEME = agniTheme;

// DEFAULT_THEME leads the list so it sits first in the switcher grid, and so
// the no-argument switchTheme() cycle starts from it.
const themesList = [
  agniTheme,
  rudraTheme,
  bhairavaTheme,
  neelkanthTheme,
  mahakalaTheme,
  gangadharTheme,
  natarajaTheme
];

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  // Initialize from storage or default
  const [theme, setThemeState] = useState(() => {
    try {
      const stored = localStorage.getItem('app_theme');
      if (stored) {
        const parsed = JSON.parse(stored);
        const found = themesList.find(t => t.name === parsed.name);
        if (found) return found;
      }
    } catch (e) {
      console.warn("Failed to load theme from storage", e);
    }
    // Default for a first-time visitor / cleared storage. A visitor who has
    // already picked a theme keeps it — the stored value above wins.
    return DEFAULT_THEME;
  });

  const setTheme = (newTheme) => {
    setThemeState(newTheme);
    try {
      localStorage.setItem('app_theme', JSON.stringify(newTheme));
    } catch (e) {
      console.error("Failed to save theme", e);
    }
  };

  // Function to switch theme (direct or cyclical)
  const switchTheme = (targetTheme) => {
    if (targetTheme) {
      setTheme(targetTheme);
    } else {
      setThemeState((current) => {
        const currentIndex = themesList.findIndex(t => t.name === current.name);
        const nextIndex = (currentIndex + 1) % themesList.length;
        const nextTheme = themesList[nextIndex];
        localStorage.setItem('app_theme', JSON.stringify(nextTheme));
        return nextTheme;
      });
    }
  };

  // Apply CSS variables on theme change (for Bootstrap and custom)
  React.useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--primary-color', theme.primary);
    root.style.setProperty('--secondary-color', theme.secondary);
    root.style.setProperty('--sidebar-bg', theme.sidebar);
    root.style.setProperty('--footer-bg', theme.footer);
    root.style.setProperty('--text-color', theme.text);
    root.style.setProperty('--card-radius', theme.cardRadius);
    root.style.setProperty('--button-radius', theme.buttonRadius);
    root.style.setProperty('--gradient-bg', theme.gradientBackground);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, switchTheme, themesList, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

// Custom hook
export function useTheme() {
  return useContext(ThemeContext);
}
